// Shared building blocks for the public website, one per component on the design boards.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from './Icon.jsx';
import { appLinks } from './navigation.js';
import { keepHomeJobDraft } from '../CoordinatedPages.jsx';

/** App icon plus the "Resident Service 360" name. The header and footer shrink it on small screens. */
export function Logo() {
  return <span className="mk-logo">
    <img className="mk-logo-icon" src="/brand/rs360-app-icon.png" alt="" width="32" height="32"/>
    <span className="mk-logo-name">Resident Service 360</span>
  </span>;
}

export function BedMapsIcon({ size = 24, className = '' }) {
  return <img className={`mk-bedmaps-icon ${className}`} src="/brand/bedmaps-app-icon.png" alt="" width={size} height={size} style={{ borderRadius: Math.round(size * 0.22) }}/>;
}

/** Sage tick with a short reassurance. */
export function Check({ children, className = '' }) {
  return <span className={`mk-check ${className}`}><Icon name="check" size={18}/>{children}</span>;
}

/** Small coloured label with an icon, used above page and card headings. */
export function PageLabel({ icon, children, className = '' }) {
  return <p className={`mk-label ${className}`}><Icon name={icon} size={20}/><span>{children}</span></p>;
}

/** Text link with an arrow. */
export function ArrowLink({ to, children, className = '' }) {
  return <Link className={`mk-arrow-link ${className}`} to={to}>{children}<Icon name="arrow-right" size={18}/></Link>;
}

/** Pill button that navigates. Variants: primary, soft, white, glass. Size is the height in pixels; big uses 17px text. */
export function ButtonLink({ to, variant = 'primary', size = 52, big = false, children, className = '' }) {
  return <Link className={`mk-button is-${variant} is-h${size} ${big ? 'is-17' : ''} ${className}`} to={to}>{children}</Link>;
}

/** Initials in a round tile. */
export function Avatar({ initials, size = 40 }) {
  return <span className="mk-avatar" style={{ width: size, height: size }} aria-hidden="true">{initials}</span>;
}

/** Board component "Quote card compact". */
export function QuoteCard({ initials = 'BP', name = 'Brook Plumbing', when = 'Tomorrow at 10', price = '£85', className = '' }) {
  return <div className={`mk-quote ${className}`}>
    <div className="mk-quote-row">
      <Avatar initials={initials}/>
      <div className="mk-quote-who"><p className="mk-quote-name">{name}</p><p className="mk-quote-when">{when}</p></div>
      <p className="mk-quote-price">{price}</p>
    </div>
    <div className="mk-quote-checks"><Check>Verified company</Check><Check>Local to you</Check></div>
  </div>;
}

export const BROOK = { initials: 'BP', name: 'Brook Plumbing', when: 'Tomorrow at 10', price: '£85' };
export const HART = { initials: 'HP', name: 'Hart Plumbing', when: 'Thursday morning', price: '£95' };

/** Board component "Service row": one trade, opening the request form with that trade filled in. */
export function ServiceRow({ icon, name, to, tone = 'heather' }) {
  return <Link className={`mk-service-row is-${tone}`} to={to}>
    <span className="mk-icon-tile is-44"><Icon name={icon} size={22}/></span>
    <span className="mk-service-name">{name}</span>
    <Icon name="chevron-right" size={18} className="mk-service-chevron"/>
  </Link>;
}

/** Board component "Menu item". */
export function MenuItem({ item, onNavigate }) {
  return <Link className="mk-menu-item" to={item.to} onClick={onNavigate}>
    <span className="mk-icon-tile is-44"><Icon name={item.icon} size={22}/></span>
    <span className="mk-menu-item-text"><span className="mk-menu-item-title">{item.title}</span><span className="mk-menu-item-line">{item.line}</span></span>
  </Link>;
}

/** Purple closing band at the foot of a page. */
export function ClosingBand({ title, text, children, wideCopy = false }) {
  return <section className="mk-closing" aria-labelledby={`closing-${title.replace(/\W+/g, '-').toLowerCase()}`}>
    <div className="mk-container mk-closing-inner">
      <div className={`mk-closing-copy ${wideCopy ? 'is-wide' : ''}`}>
        <h2 className="mk-closing-title" id={`closing-${title.replace(/\W+/g, '-').toLowerCase()}`}>{title}</h2>
        <p className="mk-closing-text">{text}</p>
      </div>
      <div className="mk-closing-actions">{children}</div>
    </div>
  </section>;
}

/**
 * "What needs fixing?" bar. It hands the visitor's words and postcode to the real home job form,
 * which asks for the rest and needs an account before anything is sent.
 */
export function RequestBar() {
  const navigate = useNavigate();
  const [problem, setProblem] = useState('');
  const [postcode, setPostcode] = useState('');
  const submit = (event) => {
    event.preventDefault();
    const words = problem.trim();
    keepHomeJobDraft({ title: words.length <= 80 ? words : '', description: words, category: '', location: postcode.trim() });
    navigate(appLinks.getQuotes);
  };
  return <form className="mk-request" onSubmit={submit}>
    <label className="mk-request-field is-problem">
      <Icon name="camera" size={24}/>
      <span className="mk-request-text">
        <span className="mk-request-label">What needs fixing?</span>
        <input className="mk-request-input" value={problem} onChange={(event) => setProblem(event.target.value)} placeholder="Add a photo or describe it" autoComplete="off"/>
      </span>
    </label>
    <label className="mk-request-field is-postcode">
      <span className="mk-request-text">
        <span className="mk-request-label">Postcode</span>
        <input className="mk-request-input" value={postcode} onChange={(event) => setPostcode(event.target.value)} placeholder="Your postcode" autoComplete="postal-code" autoCapitalize="characters" spellCheck="false"/>
      </span>
    </label>
    <button className="mk-button is-primary is-h64 is-17 mk-request-submit" type="submit">Get quotes</button>
  </form>;
}

function AppCard({ icon, label, title, line, children }) {
  return <div className="mk-app-card">
    <p className="mk-app-card-label"><Icon name={icon} size={16}/>{label}</p>
    <p className="mk-app-card-title">{title}</p>
    <p className="mk-app-card-line">{line}</p>
    {children}
  </div>;
}

/** Board component "Phone", showing the "My repairs" screen from the home page board. */
export function PhoneMyRepairs() {
  return <div className="mk-phone" role="img" aria-label="The Resident Service 360 app on a phone, showing your repairs, quotes and messages">
    <div className="mk-phone-screen">
      <div className="mk-phone-status"><span>9:41</span><span className="mk-phone-status-icons"><Icon name="signal" size={15}/><Icon name="wifi" size={15}/><Icon name="battery-full" size={18}/></span></div>
      <div className="mk-phone-bar">
        <span className="mk-phone-brand"><img src="/brand/rs360-symbol.png" alt="" width="26" height="26"/>Resident Service 360</span>
        <span className="mk-phone-place">My home</span>
      </div>
      <div className="mk-phone-body">
        <p className="mk-phone-title">My repairs</p>
        <AppCard icon="wrench" label="YOUR REPAIR" title="Dripping kitchen tap" line="Brook Plumbing"><Check>Booked for tomorrow at 10</Check></AppCard>
        <AppCard icon="receipt" label="QUOTES" title="Bathroom extractor fan" line="2 quotes so far. Take your time."/>
        <AppCard icon="message-circle" label="MESSAGE" title="Brook Plumbing" line="I will bring a new washer, just in case."/>
      </div>
    </div>
  </div>;
}
